/**
 * Cleaned-up version of SalesRepGameStatsComponent that ensures the total scanned board count from all reps matches the filteredStats unique count.
 */

import { Component, OnInit, ChangeDetectorRef, EventEmitter, Input, Output } from '@angular/core';
import { BoardStats } from 'src/app/core/models/boardStats.model';
import { BoardStatService, GroupedStats } from 'src/app/core/services/boardstats.service';
import { LoginService } from 'src/app/core/services/login.service';
import { LocationService } from 'src/app/core/services/location.service';
import { UserType } from 'src/app/core/models/userType.models';
import { BoardService } from 'src/app/core/services/board.service';

@Component({
  selector: 'app-sales-rep-game-stats',
  templateUrl: './sales-rep-game-stats.component.html',
  styleUrls: ['./sales-rep-game-stats.component.scss']
})
export class SalesRepGameStatsComponent implements OnInit {
  @Input() boardStats: any[] = [];
  @Input() startDate: Date;
  @Input() endDate: Date;
  @Input() title = 'Game Statistics by Sales Rep';
  @Input() groupBy: 'salesRep' | 'location' = 'salesRep';
  @Input() allowedNames: string[] = [];
  @Input() tableFormat: 'grouped' | 'boardStats' = 'grouped';
  @Input() flagged = false;
  @Input() isRoot = false;
  @Input() currentCategory: 'FUSION_LEASE' | 'PICKUP' | 'BOARD_SALES' | null = null;
  @Output() categoryChange = new EventEmitter<{
    stat: BoardStats;
    category: 'STANDARD' | 'FUSION_LEASE' | 'PICKUP' | 'BOARD_SALES';
  }>();
  @Output() flagChange = new EventEmitter<{ stat: BoardStats; flagged: boolean }>();
  @Output() deleteStat = new EventEmitter<BoardStats>();
  groupedStats: { salesRep: string, stats: GroupedStats, scannedBoardCount: number }[] = [];
  loading = false;
  assignedBoardsByRep: Map<string, number> = new Map();
  expandedReps: Set<string> = new Set();
  boardSortColumn = 'timeStamp';
  boardSortAscending = false;



  constructor(
    private boardStatService: BoardStatService,
    private locationService: LocationService,
    private loginService: LoginService,
    private cdr: ChangeDetectorRef,
    private boardService: BoardService
  ) {}

  sortBoardStats(column: string): void {
    if (this.boardSortColumn === column) {
      this.boardSortAscending = !this.boardSortAscending;
    } else {
      this.boardSortColumn = column;
      this.boardSortAscending = true;
    }
  }

  get sortedBoardStats(): any[] {
    const direction = this.boardSortAscending ? 1 : -1;
    return [...(this.boardStats || [])].sort((a, b) => {
      const av = this.getBoardSortValue(a, this.boardSortColumn);
      const bv = this.getBoardSortValue(b, this.boardSortColumn);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * direction;
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * direction;
    });
  }

  private getBoardSortValue(item: any, column: string): string | number | null {
    const days = Number(item?.daysSincePrevRead) || 0;
    switch (column) {
      case 'timeStamp': return item?.timeStamp ? new Date(item.timeStamp).getTime() : null;
      case 'assetTag': return item?.board?.assetTag || '';
      case 'serialNumber': return item?.board?.serialNumber || '';
      case 'location': return item?.__locationName || item?.board?.currentDeployment?.location?.locationName || '';
      case 'salesRep': return item?.__salesRepName || '';
      case 'state': return item?.__state || '';
      case 'gameName': return item?.board?.currentDeployment?.installedGamePackage?.name || item?.board?.installedGamePackage?.name || '';
      case 'cashInPerDay': return days ? Number(item?.cashInDiff) / days : 0;
      case 'netPerDay': return days ? Number(item?.profit) / days : 0;
      case 'payout': return Number(item?.holdPercentage) || 0;
      case 'rpt': return Number(item?.coinInDiff) ? Number(item?.coinOutDiff) / Number(item?.coinInDiff) : 0;
      case 'betMultiplier': return Number(item?.cashInDiff) ? Number(item?.coinInDiff) / Number(item?.cashInDiff) : 0;
      case 'betPerPlay': return Number(item?.gamePlayedDiff) ? Number(item?.coinInDiff) / Number(item?.gamePlayedDiff) : 0;
      default: return Number(item?.[column]) || 0;
    }
  }
ngOnChanges(): void {
  if (this.boardStats?.length && this.startDate && this.endDate) {
    const filtered = this.boardStats.filter(stat =>
      new Date(stat.timeStamp) >= this.startDate &&
      new Date(stat.timeStamp) <= this.endDate &&
      this.isAllowed(stat)
    );

    const grouped = this.boardStatService.groupStatsByBoardDetails(
      filtered,
      board => this.getGroupName(board)
    );

    this.groupedStats = Array.from(grouped.entries()).map(([salesRep, stats]) => ({
      salesRep,
      stats,
      scannedBoardCount: stats.rawStats.length
    }));
  }
}

  

  async ngOnInit(): Promise<void> {
    this.loading = true;

    try {
      const allLocations = await this.locationService.getAll();

      const enabledLocationIds = allLocations
        .filter(location => location.enabled === true)
        .map(location => location.locationTag);

      for (const location of allLocations) {
        if (
          location.enabled &&
          location.numBoards &&
          location.salesRep?.firstName !== 'Warehouse'
        ) {
          const rep = this.groupBy === 'location'
            ? (location.locationName || location.locationTag || 'Unknown')
            : this.boardStatService.salesRepKeyFunction({ currentDeployment: { location } } as any);
          if (!this.isAllowedName(rep)) continue;
          const current = this.assignedBoardsByRep.get(rep) || 0;
          this.assignedBoardsByRep.set(rep, current + location.numBoards);
        }
      }

const filteredStats = this.boardStats.filter(item =>
  enabledLocationIds.includes(item.board?.currentDeployment?.location?.locationTag) &&
  item.cashInDiff >= 0 &&
  item.cashOutDiff >= 0 &&
  item.board?.currentDeployment?.location?.salesRep?.firstName !== 'Warehouse' &&
  this.isAllowed(item) &&
  new Date(item.timeStamp) >= new Date(this.startDate) &&
  new Date(item.timeStamp) <= new Date(this.endDate)
);




      // Ensure netRevenue and profit exists
      filteredStats.forEach(stat => {
        (stat as any).netRevenue = stat.cashInDiff - stat.cashOutDiff;
        (stat as any).profit = stat.cashInDiff - stat.cashOutDiff;
      });

      const grouped = this.boardStatService.groupStatsByBoardDetails(
        filteredStats,
        board => this.getGroupName(board)
      );

   this.groupedStats = Array.from(grouped.entries()).map(([salesRep, stats]) => {
  return {
    salesRep,
    stats,
    scannedBoardCount: stats.rawStats.length
  };
});;

      const allFilteredBoardIds = filteredStats.map(s => s.board?.id).filter(Boolean);
      const uniqueFilteredBoardIds = new Set(allFilteredBoardIds);
      console.log('🎯 Total Filtered Unique Boards:', uniqueFilteredBoardIds.size);
      const totalGroupedUniqueBoards = this.getTotalScannedBoards();
      console.log('📊 Total Scanned (Group Sum):', totalGroupedUniqueBoards);

    } catch (err) {
      console.error('Error loading sales rep stats:', err);
    }

    this.loading = false;
    this.cdr.detectChanges();
  }

  private normalize(value: any): string {
    return (value || '').toString().trim().replace(/\s+/g, ' ').toLowerCase();
  }

  private isAllowedName(value: string): boolean {
    return !this.allowedNames.length ||
      this.allowedNames.some(name => this.normalize(name) === this.normalize(value));
  }

  private getGroupName(board: any): string {
    const location = board?.currentDeployment?.location || board?.location;
    if (this.groupBy === 'location') {
      return location?.locationName || location?.locationTag || 'Unknown';
    }
    return this.boardStatService.salesRepKeyFunction(board);
  }

  private isAllowed(stat: any): boolean {
    return this.isAllowedName(this.getGroupName(stat?.board || stat));
  }

  getGrandTotal(): GroupedStats {
    const total: GroupedStats = {
      avgDailyCashIn: 0, avgDailyNet: 0, avgDailyCoinIn: 0, avgDailyCoinOut: 0,
      avgDailyGamePlayed: 0, avgDailyGameWon: 0, avgDays: 0,
      sumDailyCashIn: 0, sumDailyNet: 0, sumDailyCoinIn: 0, sumDailyCoinOut: 0,
      sumDailyGamePlayed: 0, sumDailyGameWon: 0,
      totalCashin: 0, totalCashout: 0, totalCoinIn: 0, totalCoinOut: 0,
      totalGamesPlayed: 0, totalGamesWon: 0, totalNet: 0, totalDays: 0,
      winRatio: 0, payoutRatio: 0, rpt: 0, betMultiplier: 0, betPerPlay: 0,
      count: 0, rawStats: []
    };

    this.groupedStats.forEach(({ stats }) => {
      total.totalCashin += stats.totalCashin;
      total.totalCashout += stats.totalCashout;
      total.totalNet += stats.totalNet;
      total.totalCoinIn += stats.totalCoinIn;
      total.totalCoinOut += stats.totalCoinOut;
      total.totalGamesPlayed += stats.totalGamesPlayed;
      total.totalGamesWon += stats.totalGamesWon;
      total.totalDays += stats.totalDays;

      total.sumDailyCashIn += stats.sumDailyCashIn;
      total.sumDailyNet += stats.sumDailyNet;
      total.sumDailyCoinIn += stats.sumDailyCoinIn;
      total.sumDailyCoinOut += stats.sumDailyCoinOut;
      total.sumDailyGamePlayed += stats.sumDailyGamePlayed;
      total.sumDailyGameWon += stats.sumDailyGameWon;
      total.count += stats.count;
    });

    total.payoutRatio = total.totalCashin ? total.totalCashout / total.totalCashin : 0;
    total.rpt = total.totalCoinIn ? total.totalCoinOut / total.totalCoinIn : 0;
    total.winRatio = total.totalGamesPlayed ? total.totalGamesWon / total.totalGamesPlayed : 0;
    total.betMultiplier = total.sumDailyCashIn ? total.sumDailyCoinIn / total.sumDailyCashIn : 0;
    total.betPerPlay = total.sumDailyGamePlayed ? total.sumDailyCoinIn / total.sumDailyGamePlayed : 0;
    total.avgDays = total.count ? total.totalDays / total.count : 0;
    total.avgDailyCashIn = total.count ? total.sumDailyCashIn / total.count : 0;
    total.avgDailyNet = total.count ? total.sumDailyNet / total.count : 0;
    total.avgDailyCoinIn = total.count ? total.sumDailyCoinIn / total.count : 0;
    total.avgDailyCoinOut = total.count ? total.sumDailyCoinOut / total.count : 0;
    total.avgDailyGamePlayed = total.count ? total.sumDailyGamePlayed / total.count : 0;
    total.avgDailyGameWon = total.count ? total.sumDailyGameWon / total.count : 0;

    return total;
  }

  getUniqueBoardCount(stats: any[]): number {
    const unique = new Set(stats.map(s => s.board?.id).filter(Boolean));
    return unique.size;
  }

  getTotalScannedBoards(): number {
    return this.groupedStats.reduce((sum, group) => sum + group.scannedBoardCount, 0);
  }

  getTotalAssignedBoards(): number {
    let total = 0;
    for (const count of this.assignedBoardsByRep.values()) {
      total += count;
    }
    return total;
  }

  getAssignedBoardCount(rep: string): number {
    return this.assignedBoardsByRep.get(rep) || 0;
  }

  toggleRep(rep: string): void {
    this.expandedReps.has(rep) ? this.expandedReps.delete(rep) : this.expandedReps.add(rep);
  }

  isExpanded(rep: string): boolean {
    return this.expandedReps.has(rep);
  }

  getTotalForRep(stats: any[], field: string): number {
    return stats.reduce((sum, stat) => sum + (stat[field] || 0), 0);
  }

  getCashInPerDay(stats: any): number {
    return stats.totalDays > 0 ? stats.totalCashin / stats.totalDays : 0;
  }

  getNetPerDay(stats: any): number {
    return stats.totalDays > 0 ? stats.totalNet / stats.totalDays : 0;
  }
}
